import React, { useEffect, useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useForm } from 'react-hook-form'
import { useRequest } from '../../../../hooks/UseRequest'
import { setNotification } from '../../../../redux/notificationSlice'
import { selectDropdowns, selectDropdownOptions } from '../../../../redux/dropdownSlice'
import { setChangeMade } from '../../../../redux/paymentSchedulePanelSlice'
import { setModal } from '../../../../redux/modalSlice'
import ManagerApprovalIcons from '../../../../components/elements/ManagerApprovalIcons'
import {
  getStrategy,
  resolvePaymentPlanTypeId,
  buildDiscountBodyPatch,
  buildPromoBodyPatch,
} from './paymentPlanStrategies'
import { lookupResolutionMinPayment } from './paymentPlanMinPaymentLookup'
import { resolveApplicableMinimum, resolveLowestAvailableMinimum } from './paymentPlanApplicableMinimum'
import { resolutionMaxPaymentCount } from './paymentPlanResolutionCap'
import {
  DEFAULT_MIN_INSTALLMENT_AMOUNT,
  installmentsInTwoYears,
  getMaxSettlementDiscountFieldValue,
  isBalanceFloorBinding,
  calculatePaymentPlanBreakdown,
} from '../../../../helpers/paymentPlanHelper'

/**
 * Wizard logic for the Payment Plan flow (collection / settlement /
 * settle-for-zero). Owns all wizard state, the underlying react-hook-form
 * instance, every async handler that talks to the API, and the derived
 * dropdown-option lists.
 *
 * The parent component (`PaymentPlanPayment`) is a render-only shell that
 * consumes this hook and forwards the relevant pieces to the four step
 * components. The body-shape decisions per plan type live in
 * `paymentPlanStrategies.js`; this hook glues those decisions to runtime
 * state and form values.
 *
 * Why a hook instead of leaving the logic in the parent: the parent had ~700
 * lines of state + effects + handlers, which made it hard to spot how the
 * pieces fit together. Moving them here keeps the render shell trivial,
 * lets the wizard logic be tested independently with `@testing-library/react`'s
 * `renderHook`, and gives a single place to search for "how does the wizard
 * fetch / submit / cancel a plan."
 */
export function usePaymentPlanWizard({
  loanId,
  refreshPaymentSchedule,
  navigateSlider,
  currentDate,
  outstandingTotal,
  customerInfo,
}) {
  const dropdowns = useSelector(selectDropdowns)
  const dropdownOptions = useSelector(selectDropdownOptions)
  const requesting = useRef(false)
  const request = useRequest()
  const dispatch = useDispatch()

  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------
  const [responseData, setResponseData] = useState(null)
  const [discountData, setDiscountData] = useState(null)
  const [discountInfoText, setDiscountInfoText] = useState("")
  const [collectionResponseData, setCollectionResponseData] = useState([])
  const [settlementResponseData, setSettlementResponseData] = useState([])
  const { handleSubmit, control, setValue, watch } = useForm()
  const today = new Date().toISOString().split("T")[0]
  const [step, setStep] = useState("step1")
  const [planSelection, setPlanSelection] = useState(false)
  const strategy = getStrategy(planSelection)
  const [saving, setSaving] = useState(false)
  const [paymentPlanPaymentsStep2, setPaymentPlanPaymentsStep2] = useState([])
  const [paymentPlanPaymentsStep3, setPaymentPlanPaymentsStep3] = useState([])
  const [formBody, setFormBody] = useState({})
  const [stepOneBodyParams, setStepOneBodyParams] = useState({})
  const [stepTwoNumberOfInstallments, setStepTwoNumberOfInstallments] = useState(0)
  const [displayActionInStep4, setDisplayActionInStep4] = useState(false)
  const [totalAmount, setTotalAmount] = useState(0)
  const [payFrequencyValue, setPayFrequencyValue] = useState("")
  const [payFrequencyValueOption, setPayFrequencyValueOption] = useState("")
  const [installmentOptions, setInstallmentOptions] = useState(null)
  const [settlementDiscountType, setSettlementDiscountType] = useState("percent")
  const [settlementInstallationType, setSettlementInstallationType] = useState("number_of_installments")
  const [minPayment, setMinPayment] = useState(null)
  // Resolution returns per-method minimums from `/payment_plan/info` keyed by
  // the payment-method code key (e.g. `payment_method_ach`, `payment_method_debit_card`).
  // Step 2's per-installment validator looks up the active method here. Stays
  // empty for collection / settlement (those compute `minPayment` front-end-side
  // from frequency + first payment + outstanding).
  const [minPaymentByMethod, setMinPaymentByMethod] = useState({})
  // Resolution per-frequency cap from /payment_plan/info; empty otherwise.
  const [maxResolutionPaymentCount, setMaxResolutionPaymentCount] = useState({})
  // Balance-free per-method minimums from /payment_plan/info, for every plan type. Keyed by
  // payment-method code key. The settlement balance moves with the discount, so the wizard
  // recomputes the duration half itself rather than reading the balance-keyed grid above.
  const [minPaymentFloorByMethod, setMinPaymentFloorByMethod] = useState({})
  const [discountValue, setDiscountValue] = useState(10)
  const [collectionPromo, setCollectionPromo] = useState({})

  // ---------------------------------------------------------------------------
  // Derived dropdown options
  // ---------------------------------------------------------------------------
  const payFrequency = dropdowns.data.filter((o) => o.code_key === 'pay_frequency')[0]
  const payFrequencyOptions = dropdownOptions.filter((o) => o.dropdown_id === payFrequency.id && o.code_key != 'pay_frequency_daily').map((o) => ({ label: o.name, value: o.id }))
  const dayOfWeek = dropdowns.data.filter((o) => o.code_key === 'day_of_week')[0]
  const dayOfWeekOptions = dropdownOptions.filter((o) => o.dropdown_id === dayOfWeek.id).map((o) => ({ label: o.name, value: o.id }))
  const paymentMethod = dropdowns.data.filter((o) => o.code_key === 'payment_method')[0]
  const paymentMethodOptions = dropdownOptions.filter((o) => o.dropdown_id === paymentMethod.id).map((o) => ({ label: o.name, value: o.id }))
  const paymentMethodACHId = dropdownOptions.find((o) => o?.code_key === "payment_method_ach")?.id

  const dayOfMonthOptions = Array.from({ length: 31 }, (_, index) => {
    const day = index + 1
    return { label: day.toString() + (day < 31 ? '' : ' or Last Day of Month'), value: day.toString() }
  })

  const weekOfMonthOptions = Array.from({ length: 5 }, (_, index) => {
    const weekNumber = index + 1
    const ordinalSuffix = getOrdinalSuffix(weekNumber)
    const label = `${weekNumber}${ordinalSuffix} Week`
    return { label: label, value: weekNumber.toString() }
  })

  // ---------------------------------------------------------------------------
  // Watchers
  // ---------------------------------------------------------------------------
  // Watch exactly the amount fields in play; no hardcoded ceiling on the total.
  const fieldNames = Array.from({ length: stepTwoNumberOfInstallments }, (_, i) => `payment_amount[${i}]`)
  const watchedFields = watch(fieldNames)
  const firstPayment = watch('first_payment')
  const discountPercentStep1 = watch('discount_percent_step1')
  const discountAmountStep1 = watch('discount_amount_step1')
  const watchCustomerPromotionCodeId = watch("customer_promotion_code_id")
  const watchPaymentMethodStep1 = watch('payment_method_dropdown_option_id_step1')

  // The per-payment minimum for the method in play — the portfolio's ACH or debit minimum, the
  // flat personal-check floor, or the higher of ACH and debit before a method is picked.
  const applicableMinimum = resolveApplicableMinimum({
    floorByMethod: minPaymentFloorByMethod,
    methodId: watchPaymentMethodStep1?.value,
    dropdownOptions,
  }) ?? DEFAULT_MIN_INSTALLMENT_AMOUNT

  // The most permissive floor still open to the agent. Every gate that BLOCKS the plan uses this,
  // never applicableMinimum: refusing on max(ACH, debit) forecloses a method that still works.
  // Once a method is chosen the two agree, so this only differs before the agent has picked.
  const blockingMinimum = resolveLowestAvailableMinimum({
    floorByMethod: minPaymentFloorByMethod,
    methodId: watchPaymentMethodStep1?.value,
    dropdownOptions,
    availableMethodIds: discountData?.payment_method_dropdown_options?.map((o) => o?.id),
  }) ?? DEFAULT_MIN_INSTALLMENT_AMOUNT

  // The server-provided per-loan ceiling from /payment_plan/info; the helpers fall back
  // to MAX_SETTLEMENT_DISCOUNT_PERCENT while it hasn't loaded (or failed to load).
  const managerMaxDiscountPercent = discountData?.max_discount_percent?.manager

  // When the entered discount exceeds the cap, the breakdown controls and the Next button
  // are disabled — the agent fixes the discount (guided by the message under the input)
  // before they can proceed. Gated on the strategy flag (settlement only) so
  // settle-for-zero, which re-uses the settlement DDO, never trips it.
  const settlementDiscountCap = getMaxSettlementDiscountFieldValue(outstandingTotal, settlementDiscountType, managerMaxDiscountPercent, blockingMinimum)
  const isDiscountAboveCap = !!strategy?.validatesDiscountAgainstMax && parseFloat(outstandingTotal) > 0
    && discountValue !== '' && parseFloat(discountValue) > settlementDiscountCap

  // No plan can be built from a balance that can't fund one payment at the lowest usable floor.
  const isBalanceBelowMinimum = parseFloat(outstandingTotal) > 0 && parseFloat(outstandingTotal) < blockingMinimum
  const balanceBelowMinimumText = isBalanceBelowMinimum
    ? `The payment plan balance cannot be less than $${blockingMinimum}.`
    : null

  // ---------------------------------------------------------------------------
  // Effects
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (planSelection !== null) {
      dispatch(setChangeMade(true))
    }
  }, [planSelection])

  // Recompute the running total whenever any installment-amount field changes.
  useEffect(() => {
    const sum = Object.values(watchedFields).reduce((acc, cur) => acc + parseFloat(cur || 0), 0)
    setTotalAmount(sum)
  }, [watchedFields])

  // Populate Step 2 form fields from the latest server-computed schedule.
  // NOTE: The payment-method default assumes every installment uses the same
  // method as the first installment in `paymentPlanPaymentsStep2`.
  useEffect(() => {
    Array.from({ length: stepTwoNumberOfInstallments }, (_, index) => {
      const plan = paymentPlanPaymentsStep2[index] || {}
      setValue(`payment_date[${index}]`, plan?.payment_date ?? today)
      setValue(`payment_amount[${index}]`, plan?.total_amount?.toFixed(2) ?? 0)
      setValue(`payment_method_dropdown_option_id_step2[${index}]`, paymentMethodOptions.filter((o) => paymentPlanPaymentsStep2[0]?.payment_method_dropdown_option_id == o.value).map((o) => ({ label: o.label, value: o.value }))[0])
    })
  }, [paymentPlanPaymentsStep2, setValue, stepTwoNumberOfInstallments])

  useEffect(() => {
    getCurrentPaymentPlan(loanId)
  }, [])

  const discountPercentValue = watch('discount_percent_step1')?.toString() || ''
  const discountAmountValue = watch('discount_amount_step1')?.toString() || ''

  // Out-of-range discount surfaces a message on UI(see the discountInfoText effect below)
  // calculatePaymentPlanBreakdown clamps internally, so the min payment and installment
  // options stay sane regardless.
  useEffect(() => {
    if (settlementDiscountType === "percent") {
      setDiscountValue(discountPercentValue)
    } else if (settlementDiscountType === "amount") {
      setDiscountValue(discountAmountValue)
    }
  }, [settlementDiscountType, discountPercentValue, discountAmountValue])

  // Resolution plans source the per-installment floor from the BE
  // (minPaymentByMethod, populated by getCurrentPaymentInfo). Collection and
  // settlement keep the FE-side calculation, which applies applicableMinimum so it
  // stays live as the agent types a discount. Lookup is extracted to a pure
  // helper for unit-test coverage (paymentPlanMinPaymentLookup.test.js).
  useEffect(() => {
    const beMin = lookupResolutionMinPayment({
      planSelection,
      payFrequency,
      payFrequencyValue,
      methodId: watchPaymentMethodStep1?.value,
      dropdownOptions,
      minPaymentByMethod,
    })
    // Skip the FE breakdown fallback for resolution: the BE map is the source
    // of truth for resolution min payments, and the FE calculation's installment
    // rebuild would pin the dropdown to floor(balance/25), overriding the
    // BE-provided resolution payment cap.
    if (planSelection === 'resolution') {
      setMinPayment(beMin)
      return
    }
    if (beMin != null) {
      setMinPayment(beMin)
      return
    }
    updatePlanBreakdown()
  }, [firstPayment, payFrequencyValue, discountValue, settlementDiscountType, settlementInstallationType, planSelection, minPaymentByMethod, watchPaymentMethodStep1, outstandingTotal, discountData, applicableMinimum, blockingMinimum])

  useEffect(() => {
    setValue("number_of_installments_step1", null)
    if (planSelection === 'resolution') {
      // Resolution installment counts come from the BE-provided per-frequency
      // cap (see resolutionMaxPaymentCount) rather than the 2-year window
      // used elsewhere.
      setInstallmentOptions(buildInstallmentOptions(calculateNumberInstallment(payFrequencyValue, planSelection)))
      return
    }
    updatePlanBreakdown()
  }, [payFrequencyValue, planSelection, maxResolutionPaymentCount])

  // Discount-validation message: only runs for plans that validate against
  // agent/manager max caps (settlement). The strategy carries this flag so
  // settle-for-zero (which re-uses the settlement DDO for `/payment_plan/info`)
  // doesn't accidentally trip the validation UI.
  useEffect(() => {
    if (watchCustomerPromotionCodeId?.value) {
      setDiscountInfoText(null)
    } else if (strategy?.validatesDiscountAgainstMax && settlementDiscountType == "percent") {
      const maxSettlementPercent = getMaxSettlementDiscountFieldValue(outstandingTotal, 'percent', managerMaxDiscountPercent, blockingMinimum)
      // The floor message takes priority only when the floor (not the manager max /
      // fallback ceiling) is what limits the discount. Without this message, a 90% discount
      // on a $150 balance produced a "$Infinity" min payment with no explanation.
      if (isBalanceFloorBinding(outstandingTotal, 'percent', managerMaxDiscountPercent, blockingMinimum) && discountPercentStep1 > maxSettlementPercent) {
        setDiscountInfoText(<><ManagerApprovalIcons type="mgr_approval_status_required" /> The settlement balance cannot be reduced below ${blockingMinimum}. Maximum discount for this
          balance is {maxSettlementPercent}%.</>)
      } else if (discountPercentStep1 > managerMaxDiscountPercent) {
        setDiscountInfoText(<><ManagerApprovalIcons type="mgr_approval_status_required" /> You are not allowed to go
          over {managerMaxDiscountPercent}%.</>)
      } else if (discountPercentStep1 > discountData?.max_discount_percent?.agent) {
        setDiscountInfoText(<><ManagerApprovalIcons type="mgr_approval_status_required" /> Discount
          above {discountData?.max_discount_percent?.agent}% will require manager approval.</>)
      } else {
        setDiscountInfoText(null)
      }
    } else if (strategy?.validatesDiscountAgainstMax && settlementDiscountType == "amount") {
      const maxSettlementAmount = getMaxSettlementDiscountFieldValue(outstandingTotal, 'amount', managerMaxDiscountPercent, blockingMinimum)
      const managerMaxAmount = discountData?.max_discount_amount?.manager
      if (isBalanceFloorBinding(outstandingTotal, 'amount', managerMaxDiscountPercent, blockingMinimum) && discountAmountStep1 > maxSettlementAmount) {
        setDiscountInfoText(<><ManagerApprovalIcons type="mgr_approval_status_required" /> The settlement balance cannot be reduced below ${blockingMinimum}. Maximum discount for this
          balance is ${maxSettlementAmount}.</>)
      } else if (discountAmountStep1 > managerMaxAmount) {
        setDiscountInfoText(<><ManagerApprovalIcons type="mgr_approval_status_required" /> You are not allowed to go
          over ${managerMaxAmount}.</>)
      } else if (discountAmountStep1 > discountData?.max_discount_amount?.agent) {
        setDiscountInfoText(<><ManagerApprovalIcons type="mgr_approval_status_required" /> Discount above
          ${discountData?.max_discount_amount?.agent} will require manager approval.</>)
      } else {
        setDiscountInfoText(null)
      }
    }
  }, [discountPercentStep1, discountAmountStep1, settlementDiscountType, watchCustomerPromotionCodeId, outstandingTotal, discountData, applicableMinimum, blockingMinimum])

  useEffect(() => {
    if (responseData?.number_of_installments !== undefined) setValue('number_of_installments_step1', responseData.number_of_installments)
    if (responseData?.discount_percent !== undefined) setValue('discount_percent_step1', responseData.discount_percent)
  }, [responseData, setValue])

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------
  function getOrdinalSuffix(weekNumber) {
    switch (weekNumber) {
      case 1: return 'st'
      case 2: return 'nd'
      case 3: return 'rd'
      default: return 'th'
    }
  }

  // Payments fitting the plan window. Resolution: 48mo/200 cap (see helper).
  // Collection/settlement: 2-year cap, `per-year * years - 1` (first payment on day 0).
  function calculateNumberInstallment(frequency, planType) {
    if (planType === 'resolution') {
      return resolutionMaxPaymentCount({ frequencyLabel: frequency, maxResolutionPaymentCount })
    }
    const numberYear = 2
    switch (frequency) {
      case 'Weekly': return 52 * numberYear - 1
      case 'Bi-Weekly': return 26 * numberYear - 1
      case 'Semi-Monthly': return 24 * numberYear - 1
      case 'Monthly': return 12 * numberYear - 1
      default: return 0
    }
  }

  function buildInstallmentOptions(count) {
    return Array.from({ length: count }, (_, index) => ({
      label: index + 1,
      value: index + 1,
    }))
  }

  // Recalculates the min payment amount and the installment options together.
  // The discount is clamped inside calculatePaymentPlanBreakdown so the discounted
  // balance can never drop below the applicable minimum (which previously
  // produced a zero-installment division and an "$Infinity" min payment).
  function updatePlanBreakdown() {
    if (firstPayment && payFrequencyValue && outstandingTotal) {
      const { minPayment: newMinPayment, installmentCount } = calculatePaymentPlanBreakdown({
        outstandingTotal,
        frequency: payFrequencyValue,
        discountType: settlementDiscountType,
        discountValue,
        managerMaxPercent: managerMaxDiscountPercent,
        minInstallmentAmount: applicableMinimum,
      })
      setMinPayment(newMinPayment.toFixed(2))
      setInstallmentOptions(buildInstallmentOptions(installmentCount))
    } else {
      // No min payment until the inputs it depends on are filled; showing the bare floor
      // here read as a quoted amount that then jumped once the plan was calculated.
      setMinPayment(null)
      setInstallmentOptions(buildInstallmentOptions(installmentsInTwoYears(payFrequencyValue)))
    }
  }

  function serialize(obj, prefix) {
    const str = []
    Object.keys(obj).forEach((key) => {
      const k = prefix ? `${prefix}[${key}]` : key
      const v = obj[key]
      str.push(
        v !== null && typeof v === "object"
          ? serialize(v, k)
          : `${encodeURIComponent(k)}=${encodeURIComponent(v)}`
      )
    })
    return str.join("&")
  }

  const handleDiscountTypeChange = (type) => {
    setSettlementDiscountType(type)
  }

  const checkChangesMade = () => {
    navigateSlider("")
  }

  // ---------------------------------------------------------------------------
  // Async handlers
  // ---------------------------------------------------------------------------
  const getCurrentPaymentPlan = async (id) => {
    if (requesting.current) return
    requesting.current = true
    try {
      let endpoint = `${process.env.REACT_APP_API_DOMAIN}/loan/${id}/payment_plan/current`
      if (currentDate !== null) {
        endpoint += `?current_date=${currentDate}`
      }
      request.setEndpoint(endpoint)
      const response = await request.get()
      setResponseData(response?.data?.data)
      if (
        (response?.data?.data?.mgr_approval_status_dropdown_option_id == null ||
          response?.data?.data?.mgr_approval_status_dropdown_option_id == dropdownOptions.find((x) => x?.code_key === 'mgr_approval_status_approved')?.id)
        && response?.data?.data?.signature == null
        && response?.data?.data?.is_activated == false
      ) {
        setDisplayActionInStep4(true)
      }

      if (response?.data?.data?.payment_plan_payments?.length) {
        setStep('step0')
      }
      requesting.current = false
    } catch (error) {
      setResponseData([])
      requesting.current = false
    }
  }

  // Step-1 button handler. Sets the active plan type and pre-loads the
  // discount caps + promo data the form needs. Settle-for-zero re-uses the
  // settlement DDO when fetching `/payment_plan/info` (it has no DDO of its
  // own); the strategy module documents this overload.
  const getCurrentPaymentInfo = async (type) => {
    setDiscountValue(0)
    if (requesting.current) return
    requesting.current = true
    try {
      setPlanSelection(type)
      const requestedStrategy = getStrategy(type)
      const paymentPlanTypeDropdownOptionId = resolvePaymentPlanTypeId({
        strategy: requestedStrategy,
        dropdownOptions,
      })
      let endpoint = `${process.env.REACT_APP_API_DOMAIN}/loan/${loanId}/payment_plan/info`
      endpoint += `?payment_plan_type_dropdown_option_id=${paymentPlanTypeDropdownOptionId}`
      if (currentDate !== null) {
        endpoint += `&current_date=${currentDate}`
      }
      request.setEndpoint(endpoint)
      const response = await request.get()
      setDiscountData(response?.data?.data)
      setResponseData((prevState) => ({ ...prevState, ...{ outstanding_total: response?.data?.data.outstanding_total } }))
      setValue('discount_percent_step1', response?.data?.data?.max_discount_percent?.agent)
      setCollectionPromo(response?.data?.data?.collection_promo)
      // Resolution-only field; collection/settlement responses don't carry it.
      // Guard with the field check so toggling between plan types in one
      // session doesn't clobber prior data with undefined.
      if (response?.data?.data?.min_payment_by_method) {
        setMinPaymentByMethod(response.data.data.min_payment_by_method)
      }
      if (response?.data?.data?.min_payment_floor_by_method) {
        setMinPaymentFloorByMethod(response.data.data.min_payment_floor_by_method)
      }
      // Resolution-only; caps the installment dropdown.
      if (response?.data?.data?.max_resolution_payment_count) {
        setMaxResolutionPaymentCount(response.data.data.max_resolution_payment_count)
      }
      requesting.current = false
    } catch (error) {
      setDiscountData(null)
      requesting.current = false
    }
  }

  const onSubmit = async (formData) => {
    const paymentMethodOption = formData.payment_method_dropdown_option_id_step1?.value
    if (paymentMethodACHId == paymentMethodOption && customerInfo?.ach_revocation_active) {
      dispatch(
        setModal({
          is_active: true,
          type: 'allow_ach_payments_modal',
          options: { customerId: customerInfo.id, hasBkConfirmed: customerInfo?.has_bk_confirmed }
        })
      )
      return
    }
    // Same race as onSettleForZeroSubmit: don't gate on the shared
    // `requesting.current` ref because in-flight info/preview fetches
    // would silently no-op the submit. `saving` + the submit button's
    // `disabled={saving}` is the right re-entry guard.
    if (saving) return
    setSaving(true)

    if (step === "step1") {
      try {
        let body = {}
        body.pay_frequency_dropdown_option_id = formData.pay_frequency_dropdown_option_id.value
        body.is_pay_before_holiday = formData.is_pay_before_holiday.value
        body.payment_method_dropdown_option_id = formData.payment_method_dropdown_option_id_step1.value
        body.payment_plan_type_dropdown_option_id = resolvePaymentPlanTypeId({ strategy, dropdownOptions })
        Object.assign(body, buildPromoBodyPatch({
          strategy,
          customerPromotionCodeId: formData.customer_promotion_code_id?.value,
        }))
        if (settlementInstallationType == "number_of_installments") {
          body.number_of_installments = formData.number_of_installments_step1?.value
        } else if (settlementInstallationType == "amount") {
          body.installment_amount = formData.installment_amount
        }
        Object.assign(body, buildDiscountBodyPatch({
          strategy,
          settlementDiscountType,
          percentValue: formData.discount_percent_step1,
          amountValue: formData.discount_amount_step1,
        }))
        body.min_payment = minPayment
        body.first_payment = formData.first_payment

        if (formData.pay_frequency_dropdown_option_id.value == payFrequency.dropdown_options.filter((o) => o.code_key == "pay_frequency_weekly")[0].id) {
          body.payroll_info = {}
          body.payroll_info.payDayOfWeek = formData.pay_first_day_of_week_dropdown_option_id.label
        } else if (formData.pay_frequency_dropdown_option_id.value == payFrequency.dropdown_options.filter((o) => o.code_key == "pay_frequency_biweekly")[0].id) {
          body.payroll_info = {}
          body.payroll_info.firstPayDay = formData.first_payment
        } else if (formData.pay_frequency_dropdown_option_id.value == payFrequency.dropdown_options.filter((o) => o.code_key == "pay_frequency_semimonthly")[0].id) {
          if (formData.selection_semi_monthly.value == "Specific Days") {
            body.payroll_info = {}
            body.payroll_info.specificDays = [formData.semi_monthly_selection1_day_1.value, formData.semi_monthly_selection1_day_2.value]
          } else if (formData.selection_semi_monthly.value == "Week Day and Week of Month") {
            body.payroll_info = {}
            body.payroll_info.weekdayAndWeekOfMonth = [
              {
                dayOfWeek: formData.semi_monthly_selection2_day_1.label,
                weekOfMonth: formData.semi_monthly_selection2_week_1.value,
              },
              {
                dayOfWeek: formData.semi_monthly_selection2_day_2.label,
                weekOfMonth: formData.semi_monthly_selection2_week_2.value,
              },
            ]
          }
        } else if (formData.pay_frequency_dropdown_option_id.value == payFrequency.dropdown_options.filter((o) => o.code_key == "pay_frequency_monthly")[0].id) {
          if (formData.selection_monthly.value == "Pay Day of Month") {
            body.payroll_info = {}
            body.payroll_info.payDayOfMonth = formData.monthly_selection1_day.value
          } else if (formData.selection_monthly.value == "Week Day and Week of Month") {
            body.payroll_info = {}
            body.payroll_info.weekdayAndWeekOfMonth = {
              dayOfWeek: formData.monthly_selection2_day_1.label,
              weekOfMonth: formData.monthly_selection2_week_1.value,
            }
          }
        }

        // Step 2 needs the same body if the agent changes the installment count.
        setStepOneBodyParams(body)
        const queryString = serialize(body)
        let endpoint = `${process.env.REACT_APP_API_DOMAIN}/loan/${loanId}/payment_plan/setup?${queryString}`
        if (currentDate !== null) {
          endpoint += `&current_date=${currentDate}`
        }
        request.setEndpoint(endpoint)
        const response = await request.get(body)
        if (response.status === 200 || response.status === 201) {
          setSaving(false)
          setCollectionResponseData(response?.data?.data)
          setPaymentPlanPaymentsStep2(response?.data?.data?.payment_plan_payments)
          setStepTwoNumberOfInstallments(response?.data?.data?.number_of_installments)
          setValue('number_of_installments_step2', { label: response?.data?.data?.number_of_installments, value: response?.data?.data?.number_of_installments })
          setStep("step2")
        } else {
          setSaving(false)
          const errorMessage = response?.data?.message ?? response?.data?.messageExtreme
          dispatch(setNotification({ type: 'error', body: `${errorMessage}` }))
        }
      } catch (error) {
        setSaving(false)
        const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
        dispatch(setNotification({ type: 'error', body: errorMessage }))
      }
    } else if (step === "step2") {
      try {
        let body = {}
        body.payment_plan_type_dropdown_option_id = resolvePaymentPlanTypeId({ strategy, dropdownOptions })
        Object.assign(body, buildDiscountBodyPatch({
          strategy,
          settlementDiscountType,
          percentValue: collectionResponseData?.discount_percent,
          amountValue: collectionResponseData?.discount_amount,
        }))
        Object.assign(body, buildPromoBodyPatch({
          strategy,
          customerPromotionCodeId: watchCustomerPromotionCodeId?.value,
        }))

        body.payment_date = []
        body.payment_amount = []
        body.payment_method_dropdown_option_id = []

        for (let index = 0; index < stepTwoNumberOfInstallments; index++) {
          if (Array.isArray(formData.payment_date) && formData.payment_date[index] !== undefined) {
            body.payment_date[index] = formData.payment_date[index]
          }
          if (Array.isArray(formData.payment_amount) && formData.payment_amount[index] !== undefined) {
            body.payment_amount[index] = formData.payment_amount[index]
          }
          if (Array.isArray(formData.payment_method_dropdown_option_id_step2) && formData.payment_method_dropdown_option_id_step2[index] !== undefined) {
            body.payment_method_dropdown_option_id[index] = formData.payment_method_dropdown_option_id_step2[index].value
          }
        }

        let endpoint = `${process.env.REACT_APP_API_DOMAIN}/loan/${loanId}/payment_plan/preview`
        if (currentDate !== null) {
          body.current_date = currentDate
        }
        request.setEndpoint(endpoint)
        const response = await request.post(body)
        if (response.status === 200 || response.status === 201) {
          setSaving(false)
          setFormBody(body)
          setSettlementResponseData(response?.data?.data)
          setPaymentPlanPaymentsStep3(response?.data?.data?.payment_plan_payments)
          setStep("step3")
        } else {
          setSaving(false)
          dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }))
        }
      } catch (error) {
        setSaving(false)
        const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
        dispatch(setNotification({ type: 'error', body: errorMessage }))
      }
    } else if (step === "step3") {
      try {
        let body = formBody
        Object.assign(body, buildDiscountBodyPatch({
          strategy,
          settlementDiscountType,
          percentValue: settlementResponseData?.discount_percent,
          amountValue: settlementResponseData?.discount_amount,
        }))
        if (currentDate !== null) {
          body.current_date = currentDate
        }
        Object.assign(body, buildPromoBodyPatch({
          strategy,
          customerPromotionCodeId: watchCustomerPromotionCodeId?.value,
        }))

        // Per-plan-type endpoint comes from the strategy. Collection /
        // settlement → /payment_plan. Resolution branches on whether a thin
        // plan was loaded at modal open — flesh-existing vs create-new
        // (see RESOLUTION strategy comment for the two-flow rationale).
        const hasExistingThinPlan = !!responseData?.id
        let endpoint = `${process.env.REACT_APP_API_DOMAIN}${strategy.submitEndpoint(loanId, { hasExistingThinPlan })}`
        request.setEndpoint(endpoint)
        const response = await request.post(body)
        if (response.status === 200 || response.status === 201) {
          setSaving(false)
          dispatch(setNotification({ type: 'success', body: '' }))
          navigateSlider("form_submit")
          refreshPaymentSchedule()
        } else {
          setSaving(false)
          dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }))
        }
      } catch (error) {
        setSaving(false)
        const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
        dispatch(setNotification({ type: 'error', body: errorMessage }))
      }
    } else if (step === "step4") {
      try {
        let body = {}
        if (currentDate !== null) {
          body.current_date = currentDate
        }
        Object.assign(body, buildPromoBodyPatch({
          strategy,
          customerPromotionCodeId: watchCustomerPromotionCodeId?.value,
        }))

        let endpoint = `${process.env.REACT_APP_API_DOMAIN}/loan/${loanId}/payment_plan/${responseData?.id}/start`
        if (responseData?.is_started == true && responseData?.is_activated == false) {
          endpoint = `${process.env.REACT_APP_API_DOMAIN}/loan/${loanId}/payment_plan/${responseData?.id}/send_agreement`
        }
        request.setEndpoint(endpoint)
        const response = await request.modify(body)
        if (response.status === 200 || response.status === 201) {
          setSaving(false)
          dispatch(setNotification({ type: 'success', body: '' }))
          navigateSlider("form_submit")
          refreshPaymentSchedule()
        } else {
          setSaving(false)
          dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }))
        }
      } catch (error) {
        setSaving(false)
        const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
        dispatch(setNotification({ type: 'error', body: errorMessage }))
      }
    }
  }

  const onSettleForZeroSubmit = async () => {
    // Don't gate on `requesting.current` — that ref is shared with
    // getCurrentPaymentInfo/getCurrentPaymentPlan/refreshStepTwo and stays
    // true while an info-fetch is in flight, which races against the
    // submit click and silently no-ops the request. The local `saving`
    // state + the submit button's `disabled={saving}` is the actual
    // re-entry guard for this handler.
    if (saving) return
    setSaving(true)
    try {
      let body = {}
      // Strategy is always settleforzero here (this handler is only wired up
      // when the settle-for-zero form renders). Reading via strategy keeps the
      // endpoint URL co-located with the rest of the per-plan-type config.
      let endpoint = `${process.env.REACT_APP_API_DOMAIN}${strategy.submitEndpoint(loanId)}`
      if (currentDate !== null) {
        body.current_date = currentDate
      }
      request.setEndpoint(endpoint)
      const response = await request.post(body)
      if (response.status === 200 || response.status === 201) {
        setSaving(false)
        dispatch(setNotification({ type: 'success', body: '' }))
        navigateSlider("form_submit")
        refreshPaymentSchedule()
      } else {
        setSaving(false)
        dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }))
      }
    } catch (error) {
      setSaving(false)
      const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
      dispatch(setNotification({ type: 'error', body: errorMessage }))
    }
  }

  // Re-runs the Step-1 setup with a new installment count when the agent
  // changes "# of Installments" on Step 2. Re-uses the body params captured
  // when the user submitted Step 1.
  const refreshStepTwo = async (value) => {
    setSaving(true)
    try {
      setStepTwoNumberOfInstallments(value)
      let body = stepOneBodyParams
      body.number_of_installments = value
      const queryString = serialize(body)
      let endpoint = `${process.env.REACT_APP_API_DOMAIN}/loan/${loanId}/payment_plan/setup?${queryString}`
      if (currentDate !== null) {
        endpoint += `&current_date=${currentDate}`
      }
      request.setEndpoint(endpoint)
      const response = await request.get(body)
      if (response.status === 200 || response.status === 201) {
        setSaving(false)
        setCollectionResponseData(response?.data?.data)
        setPaymentPlanPaymentsStep2(response?.data?.data?.payment_plan_payments)
        setStep("step2")
      } else {
        setSaving(false)
        dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }))
      }
    } catch (error) {
      setSaving(false)
      const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
      dispatch(setNotification({ type: 'error', body: errorMessage }))
    }
  }

  const deletePaymentPlanSuccessCallback = () => {
    // Capture the cancelled plan's type before we wipe responseData; resolution
    // cancels need a post-cancel refetch (see below), collection/settlement
    // don't.
    const cancelledResolutionPlan = !!responseData?.payment_plan_type_dropdown_option_id
      && dropdownOptions.find(
          (o) => o?.id === responseData.payment_plan_type_dropdown_option_id,
        )?.code_key === 'payment_plan_type_resolution'

    setDisplayActionInStep4(false)
    setStep('step1')
    requesting.current = false
    // Refresh the parent panel so the resolution-substatus flags
    // (`isInResolutionWithoutPlan`) flip to reflect post-cancel state.
    // Without this, a freshly-cancelled resolution plan leaves the wizard's
    // Step 1 with the stale collection/settlement buttons instead of the
    // Create Resolution Plan button the loan now wants. Collection and
    // settlement cancellations don't depend on a substatus update — the
    // loan stays in collections — so this is functionally a no-op for those.
    if (typeof refreshPaymentSchedule === 'function') {
      refreshPaymentSchedule()
    }
    if (cancelledResolutionPlan) {
      // BE chains createResolutionThinPlan after a resolution cancel
      // (PaymentPlanActionController::destroyPaymentPlan). Refetch so
      // responseData / hasExistingThinPlan reflect the new thin plan —
      // otherwise the next submit routes through storePaymentPlan instead of
      // the flesh endpoint and 500s when the create path tries to cancel the
      // thin plan it didn't know existed.
      getCurrentPaymentPlan(loanId)
    } else {
      // Collection / settlement cancels leave the loan planless; no refetch
      // (and the in-flight request would race with the agent's next plan-type
      // click through requesting.current).
      setResponseData([])
    }
  }

  const deletePaymentPlan = async () => {
    if (requesting.current) return
    try {
      const paymentPlanId = responseData?.id
      await dispatch(setModal({
        is_active: true,
        type: 'delete_record',
        options: {
          baseName: `loan/${loanId}/payment_plan`,
          id: paymentPlanId,
          currentDate: currentDate,
          confirmText: 'Are you sure want to delete current payment plan ?',
          onSuccess: deletePaymentPlanSuccessCallback,
        }
      }))
    } catch (error) {
      dispatch(setNotification({ type: 'error', body: error.response?.data?.message }))
      requesting.current = false
    }
  }

  // ---------------------------------------------------------------------------
  // Public surface
  // ---------------------------------------------------------------------------
  return {
    // Step state
    step,
    setStep,
    planSelection,
    strategy,
    saving,

    // Server data
    responseData,
    discountData,
    discountInfoText,
    collectionResponseData,
    settlementResponseData,
    paymentPlanPaymentsStep2,
    paymentPlanPaymentsStep3,
    formBody,
    displayActionInStep4,
    collectionPromo,

    // Wizard scratch state
    stepTwoNumberOfInstallments,
    setStepTwoNumberOfInstallments,
    totalAmount,
    payFrequencyValue,
    setPayFrequencyValue,
    payFrequencyValueOption,
    setPayFrequencyValueOption,
    installmentOptions,
    // Max payments for the selected frequency (Step 1 tooltip).
    resolutionMaxPayments: planSelection === 'resolution'
      ? calculateNumberInstallment(payFrequencyValue, planSelection)
      : null,
    minPayment,
    minPaymentByMethod,
    settlementDiscountType,
    setSettlementDiscountType,
    settlementInstallationType,
    setSettlementInstallationType,
    setDiscountValue,

    // RHF
    control,
    handleSubmit,
    setValue,

    // Watchers (Step 3 needs raw values for "Send for Approval" copy)
    watchCustomerPromotionCodeId,
    discountPercentStep1,
    discountAmountStep1,

    // Derived options
    payFrequencyOptions,
    dayOfWeekOptions,
    dayOfMonthOptions,
    weekOfMonthOptions,

    // Misc
    today,
    dropdownOptions,

    // Handlers
    onSubmit,
    onSettleForZeroSubmit,
    getCurrentPaymentInfo,
    refreshStepTwo,
    checkChangesMade,
    handleDiscountTypeChange,
    deletePaymentPlan,
    deletePaymentPlanSuccessCallback,
    getCurrentPaymentPlan,
    isDiscountAboveCap,
    isBalanceBelowMinimum,
    balanceBelowMinimumText,
  }
}
